import type Home from '@/views/Home';
import setupCommunityFireworks from './communityFireworks';

export default function setupLandingEffects(this: Home) {
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
    window.scrollTo(0, 0);

    const hero = document.getElementById('section-1');
    const section2 = document.getElementById('section-2');
    const section3 = document.getElementById('section-3');
    const section4 = document.getElementById('section-4');
    const section5 = document.getElementById('section-5');
    const section6 = document.getElementById('section-6');
    const socialStack = document.getElementById('socialStack');
    const socialYoutubeIcon = document.getElementById('socialYoutubeIcon') as HTMLImageElement | null;
    const loginBtn = document.getElementById('loginBtn');
    const loginBtnLabel = document.getElementById('loginBtnLabel');
    const heroAuthBtnLabel = document.getElementById('heroAuthBtnLabel');
    const heroAuthBtn = heroAuthBtnLabel?.closest('.hero-auth-btn');
    const heroNextBtnLabel = document.getElementById('heroNextBtnLabel');
    const heroNextBtn = heroNextBtnLabel?.closest('.hero-next-btn');
    const legalStrip = document.getElementById('legalStrip');
    const currentYear = document.getElementById('currentYear');
    const card = document.getElementById('scrollCard');
    const shimmerLayer = card?.querySelector('.scroll-card__shimmer') as HTMLElement | null;
    const section2Narrative = document.getElementById('section2Narrative');
    const section2SwapWord = document.getElementById('section2SwapWord');
    const section3Narrative = document.getElementById('section3Narrative');
    const section3SwapWord = document.getElementById('section3SwapWord');
    const section3DevsImg = document.getElementById('section3DevsImg');
    const section4Carousel = document.getElementById('section4Carousel') as HTMLElement | null;
    const section4CarouselTrack = document.getElementById('section4CarouselTrack') as HTMLElement | null;
    const section4NavUp = document.getElementById('section4NavUp') as HTMLButtonElement | null;
    const section4NavDown = document.getElementById('section4NavDown') as HTMLButtonElement | null;
    const section6Panel = section6?.querySelector('.section-6__panel') as HTMLElement | null;
    const section6BottomIcon = section6?.querySelector('.section-6__bottom-icon') as HTMLImageElement | null;
    const section2GlobalDimLayer = this.$refs.section2GlobalDim as HTMLElement | undefined;
    const section2GlobalBlurLayer = this.$refs.section2GlobalBlur as HTMLElement | undefined;
    const bodyEl = document.body;
    const heroIntroSplash = document.getElementById('heroIntroSplash');

    if (
      !section2 ||
      !section3 ||
      !section4 ||
      !section5 ||
      !section6 ||
      !socialStack ||
      !loginBtn ||
      !loginBtnLabel ||
      !legalStrip ||
      !currentYear ||
      !card ||
      !section2Narrative ||
      !section2SwapWord ||
      !section3Narrative ||
      !section3SwapWord ||
      !section3DevsImg ||
      !shimmerLayer ||
      !section4Carousel ||
      !section4CarouselTrack
    ) {
      return;
    }

    currentYear.textContent = String(new Date().getFullYear());
    hero?.style.setProperty('--hero-scroll-dim', '0');
    section2.style.setProperty('--s2-entry-shadow', '0');
    section2.style.setProperty('--s2-narrative-dim', '0');
    section2.style.setProperty('--s2-narrative-blur', '0');
    section2.style.setProperty('--s2-overlay-opacity', '1');

    const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));
    const sectionNodes = this.landingSectionIds
      .map((sectionId) => document.getElementById(sectionId))
      .filter((node): node is HTMLElement => Boolean(node));
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const communityFireworks = setupCommunityFireworks(section6, prefersReducedMotion);
    this.landingCleanup.push(communityFireworks.destroy);
    const section4Slides = Array.from(section4CarouselTrack.querySelectorAll('.section4-carousel__slide')) as HTMLElement[];

    let lastSwap = 0;
    let lastCardOpacity = 0;
    let liveSwapProgress = 0;
    let liveSection3EntryProgress = 0;
    let section3DevsSwapped = false;
    let loginLabelToggle = false;
    let heroNextLabelToggle = false;
    let shimmerTriggered = false;
    let prevSwapProgress = 0;
    let section2To3Timer: number | null = null;
    let section3To4Timer: number | null = null;
    let lastScrollY = window.scrollY;
    let scrollDirection = 0;
    let scrollEndTimer: number | null = null;
    let snapRafId: number | null = null;
    let snapTargetY: number | null = null;
    let gestureActive = false;
    let lastWheelAt = 0;
    let introSequenceStarted = false;
    let section2WordTimer: number | null = null;
    let section2DetailTimer: number | null = null;
    let section2RestTimer: number | null = null;
    let section2RevealTimer: number | null = null;
    let section2IntroDelayTimer: number | null = null;
    let section2IntroDimTimer: number | null = null;
    let section2ExitTextTimer: number | null = null;
    let section2ExitBlurTimer: number | null = null;
    let section2WordIndex = 0;
    let section2WordDeleting = false;
    let section2NarrativeVisible = false;
    let section2NarrativePending = false;
    let section2NarrativeExiting = false;
    let section2SuppressFx = false;
    let narrativeDimForced = false;
    let narrativeBlurForced = false;
    let section3WordTimer: number | null = null;
    let section3DetailTimer: number | null = null;
    let section3RestTimer: number | null = null;
    let section3IntroDelayTimer: number | null = null;
    let section3IntroDimTimer: number | null = null;
    let section3ExitTextTimer: number | null = null;
    let section3ReadyTimer: number | null = null;
    let section3NarrativeGateTimer: number | null = null;
    let section3WordIndex = 0;
    let section3WordDeleting = false;
    let section3NarrativeVisible = false;
    let section3NarrativePending = false;
    let section3NarrativeExiting = false;
    let section3SuppressFx = false;
    let section3NarrativeDimForced = false;
    let section3NarrativeBlurForced = false;
    let section3ReadyAfterFlipDelay = false;
    let section3NarrativeReadyAfterDev = false;
    let section4ActiveIndex = 0;
    let section4ScrollRafId: number | null = null;
    let section4IsMouseDragging = false;
    let section4DragStartCoord = 0;
    let section4DragStartScroll = 0;
    let section4DragTravel = 0;
    let section4SuppressClickUntil = 0;
    let section4RebaseTimer: number | null = null;
    let section4ProgrammaticScrollUntil = 0;
    let section4ReleaseSnapTimer: number | null = null;
    let section4AutoplayTimer: number | null = null;
    let section4AutoplayResumeAt = 0;
    let lastGlobalDimOpacity = -1;
    let lastGlobalBlurOpacity = -1;
    let lastLoginToneDark = false;
    let lastLegalStripTransparent = false;
    const section3Words = [
      'prosty.',
      'łatwy.',
      'ładny.',
      'szybki.',
      'zgrabny.',
      'płynny.',
      'lekki.',
      'sprawny.',
      'czytelny.',
      'jasny.',
      'wygodny.',
      'miły.',
      'gładki.',
      'zwinny.',
      'spójny.',
      'pewny.',
      'świeży.',
    ];
    const section2Words = ['trudne?', 'drogie?', 'żmudne?', 'nudne?', 'ciężkie?', 'toporne?', 'mozolne?', 'długie?'];
    const section4AutoplayIntervalMs = 5200;
    const section4AutoplayInteractionPauseMs = 30000;
    const section2NarrativeRevealSwapThreshold = 0.8;
    const section2NarrativeRevealOpacityThreshold = 0.5;
    const section2NarrativePreDimDelay = prefersReducedMotion.matches ? 80 : 420;
    const section2NarrativeDimInDuration = prefersReducedMotion.matches ? 80 : 320;
    const section2NarrativeHeroHoldDelay = prefersReducedMotion.matches ? 90 : 420;
    const section2NarrativeRestDelay = prefersReducedMotion.matches ? 220 : 2120;
    const section3NarrativeRevealHoldDelay = prefersReducedMotion.matches ? 90 : 300;
    const sectionNarrativeDetailDelay = prefersReducedMotion.matches ? 120 : 1120;
    const sectionNarrativeRestDelay = prefersReducedMotion.matches ? 180 : 1880;
    const section3DevSwapStartDelay = prefersReducedMotion.matches ? 60 : 140;
    const section3NarrativeAfterDevDelay = prefersReducedMotion.matches ? 120 : 240;
    const section2NarrativeTextFadeDuration = 380;
    const section2TypeSpeed = 95;
    const section2DeleteSpeed = 58;
    const section2TypePauseFull = 880;
    const section2TypePauseEmpty = 180;
    const isSection4Vertical = () => window.matchMedia('(max-width: 959px), (orientation: portrait) and (max-width: 1100px)').matches;

    const getSection4ViewportCenter = () => (
      isSection4Vertical()
        ? section4CarouselTrack.scrollTop + section4CarouselTrack.clientHeight * 0.5
        : section4CarouselTrack.scrollLeft + section4CarouselTrack.clientWidth * 0.5
    );

    const getSection4SlideCenter = (slide: HTMLElement) => (
      isSection4Vertical()
        ? slide.offsetTop + slide.offsetHeight * 0.5
        : slide.offsetLeft + slide.offsetWidth * 0.5
    );

    const getSection4TargetOffset = (slide: HTMLElement) => (
      isSection4Vertical()
        ? slide.offsetTop - (section4CarouselTrack.clientHeight - slide.offsetHeight) / 2
        : slide.offsetLeft - (section4CarouselTrack.clientWidth - slide.offsetWidth) / 2
    );

    const scrollSection4TrackTo = (targetOffset: number, behavior: ScrollBehavior) => {
      if (isSection4Vertical()) {
        section4CarouselTrack.scrollTo({ top: targetOffset, behavior });
        return;
      }
      section4CarouselTrack.scrollTo({ left: targetOffset, behavior });
    };

    const updateLoginTone = () => {
      const isDark = this.useLightLoginTheme;
      const isLastSectionActive = this.currentSectionIndex === this.landingSectionIds.length - 1;

      if (isDark !== lastLoginToneDark) {
        socialStack.classList.toggle('is-dark-bg', isDark);
        loginBtn.classList.toggle('is-dark-bg', isDark);
        legalStrip.classList.toggle('is-dark-bg', isDark);
        if (socialYoutubeIcon) {
          socialYoutubeIcon.src = isDark ? '/main/social_youtube_light.svg' : '/main/social_youtube.svg';
        }
        lastLoginToneDark = isDark;
      }

      if (isLastSectionActive !== lastLegalStripTransparent) {
        legalStrip.classList.toggle('is-transparent', isLastSectionActive);
        lastLegalStripTransparent = isLastSectionActive;
      }
    };

    const rotateLoginLabel = () => {
      loginBtn.classList.add('is-label-fading');
      heroAuthBtn?.classList.add('is-label-fading');
      window.setTimeout(() => {
        loginLabelToggle = !loginLabelToggle;
        const nextLabel = loginLabelToggle ? 'Załóż konto' : 'Zaloguj się';
        loginBtnLabel.textContent = nextLabel;
        if (heroAuthBtnLabel) {
          const heroLabel = nextLabel.toLocaleUpperCase('pl-PL');
          heroAuthBtnLabel.textContent = heroLabel;
          heroAuthBtnLabel.dataset.shine = heroLabel;
        }
        loginBtn.classList.remove('is-label-fading');
        heroAuthBtn?.classList.remove('is-label-fading');
      }, 260);
    };

    const rotateHeroNextLabel = () => {
      heroNextBtn?.classList.add('is-label-fading');
      window.setTimeout(() => {
        heroNextLabelToggle = !heroNextLabelToggle;
        if (heroNextBtnLabel) {
          heroNextBtnLabel.textContent = heroNextLabelToggle ? 'ZOBACZ' : 'DALEJ';
        }
        heroNextBtn?.classList.remove('is-label-fading');
      }, 180);
    };

    const scrollSection4ToIndex = (nextIndex: number, behavior: ScrollBehavior = 'smooth') => {
      if (section4Slides.length === 0) { return; }
      const logicalSlidesCount = this.section4Screens.length;
      const clampedLogicalIndex = clamp(nextIndex, 0, logicalSlidesCount - 1);
      section4ActiveIndex = clampedLogicalIndex;
      // Always target the middle copy to avoid edge rebases during smooth scrolling.
      const targetPhysicalIndex = logicalSlidesCount + clampedLogicalIndex;
      const targetSlide = section4Slides[targetPhysicalIndex];
      if (!targetSlide) { return; }
      const targetOffset = getSection4TargetOffset(targetSlide);
      section4ProgrammaticScrollUntil = performance.now() + (behavior === 'smooth' ? 900 : 120);
      scrollSection4TrackTo(targetOffset, behavior);
    };

    const stepSection4Index = (direction: number) => {
      const logicalSlidesCount = this.section4Screens.length;
      if (logicalSlidesCount === 0) { return; }
      markSection4Interaction();
      const nextIndex = (section4ActiveIndex + direction + logicalSlidesCount) % logicalSlidesCount;
      scrollSection4ToIndex(nextIndex);
    };

    const markSection4Interaction = () => {
      section4AutoplayResumeAt = performance.now() + section4AutoplayInteractionPauseMs;
    };


    const rebaseSection4LoopPosition = (closestPhysicalIndex: number) => {
      const logicalSlidesCount = this.section4Screens.length;
      if (logicalSlidesCount === 0 || section4Slides.length < logicalSlidesCount * 3) { return; }
      if (closestPhysicalIndex >= logicalSlidesCount && closestPhysicalIndex < logicalSlidesCount * 2) { return; }

      const sourceSlide = section4Slides[closestPhysicalIndex];
      const logicalIndex = Number(sourceSlide.dataset.slideIndex);
      if (Number.isNaN(logicalIndex)) { return; }
      const middlePhysicalIndex = logicalSlidesCount + logicalIndex;
      const middleSlide = section4Slides[middlePhysicalIndex];
      if (!middleSlide) { return; }
      const targetOffset = getSection4TargetOffset(middleSlide);
      scrollSection4TrackTo(targetOffset, 'auto');
    };

    const scheduleSection4Rebase = () => {
      if (section4RebaseTimer !== null) {
        clearTimeout(section4RebaseTimer);
      }
      section4RebaseTimer = window.setTimeout(() => {
        section4RebaseTimer = null;
        if (section4IsMouseDragging) { return; }
        if (performance.now() < section4ProgrammaticScrollUntil) { return; }
        const viewportCenter = getSection4ViewportCenter();
        let closestPhysicalIndex = 0;
        let closestDistance = Number.POSITIVE_INFINITY;
        section4Slides.forEach((slide, index) => {
          const slideCenter = getSection4SlideCenter(slide);
          const distance = Math.abs(slideCenter - viewportCenter);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestPhysicalIndex = index;
          }
        });
        rebaseSection4LoopPosition(closestPhysicalIndex);
      }, 110);
    };

    const getSection4ClosestPhysicalIndex = () => {
      const viewportCenter = getSection4ViewportCenter();
      let closestPhysicalIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;
      section4Slides.forEach((slide, index) => {
        const slideCenter = getSection4SlideCenter(slide);
        const distance = Math.abs(slideCenter - viewportCenter);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestPhysicalIndex = index;
        }
      });
      return closestPhysicalIndex;
    };

    const centerSection4NearestSlide = (behavior: ScrollBehavior = 'smooth') => {
      if (section4Slides.length === 0) { return; }
      const closestPhysicalIndex = getSection4ClosestPhysicalIndex();
      const closestSlide = section4Slides[closestPhysicalIndex];
      if (!closestSlide) { return; }
      const targetOffset = getSection4TargetOffset(closestSlide);
      section4ProgrammaticScrollUntil = performance.now() + (behavior === 'smooth' ? 520 : 120);
      scrollSection4TrackTo(targetOffset, behavior);
    };

    const updateSection4ActiveSlide = () => {
      if (section4Slides.length === 0) { return; }
      const closestPhysicalIndex = getSection4ClosestPhysicalIndex();
      const closestLogicalIndex = Number(section4Slides[closestPhysicalIndex].dataset.slideIndex);
      section4ActiveIndex = Number.isNaN(closestLogicalIndex) ? 0 : closestLogicalIndex;
      section4Slides.forEach((slide, index) => {
        slide.classList.toggle('is-active', index === closestPhysicalIndex);
      });
      scheduleSection4Rebase();
    };

    const onSection4TrackScroll = () => {
      if (section4ScrollRafId !== null) { return; }
      section4ScrollRafId = requestAnimationFrame(() => {
        section4ScrollRafId = null;
        updateSection4ActiveSlide();
      });
    };

    const flipSection4Slide = (slide: HTMLElement, event: MouseEvent) => {
      const rect = slide.getBoundingClientRect();
      const clickedLeft = event.clientX < rect.left + (rect.width / 2);
      slide.style.setProperty('--s4-flip-angle', clickedLeft ? '-180deg' : '180deg');
      slide.classList.toggle('is-flipped');
    };

    const setSection4Cursor = (cursor: 'grab' | 'grabbing') => {
      section4Carousel.style.cursor = cursor;
      section4CarouselTrack.style.cursor = cursor;
      section4Slides.forEach((slide) => {
        slide.style.cursor = cursor;
        const picture = slide.querySelector('picture') as HTMLElement | null;
        const image = slide.querySelector('.section4-carousel__img') as HTMLElement | null;
        if (picture) { picture.style.cursor = cursor; }
        if (image) { image.style.cursor = cursor; }
      });
    };

    setSection4Cursor('grab');

    const onSection4TrackMouseDown = (event: MouseEvent) => {
      if (event.button !== 0) { return; }
      if (section4Carousel.classList.contains('is-transitioning')) { return; }
      markSection4Interaction();
      section4IsMouseDragging = true;
      bodyEl.classList.add('section4-is-dragging');
      section4Carousel.classList.add('is-dragging');
      setSection4Cursor('grabbing');
      section4DragStartCoord = isSection4Vertical() ? event.clientY : event.clientX;
      section4DragStartScroll = isSection4Vertical() ? section4CarouselTrack.scrollTop : section4CarouselTrack.scrollLeft;
      section4DragTravel = 0;
      section4CarouselTrack.classList.add('is-dragging');
      event.preventDefault();
    };

    const onSection4TrackMouseMove = (event: MouseEvent) => {
      if (!section4IsMouseDragging) { return; }
      markSection4Interaction();
      const pointerCoord = isSection4Vertical() ? event.clientY : event.clientX;
      const dragDelta = pointerCoord - section4DragStartCoord;
      section4DragTravel = Math.max(section4DragTravel, Math.abs(dragDelta));
      if (isSection4Vertical()) {
        section4CarouselTrack.scrollTop = section4DragStartScroll - dragDelta;
      } else {
        section4CarouselTrack.scrollLeft = section4DragStartScroll - dragDelta;
      }
      event.preventDefault();
    };

    const endSection4MouseDrag = () => {
      if (!section4IsMouseDragging) { return; }
      section4IsMouseDragging = false;
      bodyEl.classList.remove('section4-is-dragging');
      section4Carousel.classList.remove('is-dragging');
      setSection4Cursor('grab');
      if (section4DragTravel > 8) {
        section4SuppressClickUntil = performance.now() + 220;
      }
      if (section4DragTravel > 1) {
        section4CarouselTrack.classList.add('is-release-snapping');
        centerSection4NearestSlide('smooth');
        if (section4ReleaseSnapTimer !== null) {
          clearTimeout(section4ReleaseSnapTimer);
        }
        section4ReleaseSnapTimer = window.setTimeout(() => {
          section4ReleaseSnapTimer = null;
          section4CarouselTrack.classList.remove('is-dragging', 'is-release-snapping');
        }, prefersReducedMotion.matches ? 220 : 560);
      } else {
        section4CarouselTrack.classList.remove('is-dragging', 'is-release-snapping');
      }
      section4DragTravel = 0;
      scheduleSection4Rebase();
    };

    const onSection4TrackClick = (event: MouseEvent) => {
      if (isSection4Vertical()) { return; }
      if (section4Carousel.classList.contains('is-transitioning')) { return; }
      const clickedSlide = (event.target as HTMLElement | null)?.closest('.section4-carousel__slide') as HTMLElement | null;
      if (!clickedSlide) { return; }
      if (performance.now() < section4SuppressClickUntil) { return; }
      markSection4Interaction();
      if (!clickedSlide.classList.contains('is-active')) {
        const slideIndexAttr = clickedSlide.dataset.slideIndex;
        const slideIndex = slideIndexAttr ? Number(slideIndexAttr) : NaN;
        if (!Number.isNaN(slideIndex)) {
          scrollSection4ToIndex(slideIndex);
        }
        return;
      }
      flipSection4Slide(clickedSlide, event);
    };

    const onSection4ImageDragStart = (event: DragEvent) => {
      event.preventDefault();
    };

    const onSection4NavUpClick = (event: MouseEvent) => {
      if (!isSection4Vertical()) { return; }
      event.preventDefault();
      stepSection4Index(-1);
    };

    const onSection4NavDownClick = (event: MouseEvent) => {
      if (!isSection4Vertical()) { return; }
      event.preventDefault();
      stepSection4Index(1);
    };

    const scheduleSection4Autoplay = () => {
      if (isSection4Vertical()) {
        if (section4AutoplayTimer !== null) {
          clearTimeout(section4AutoplayTimer);
          section4AutoplayTimer = null;
        }
        return;
      }
      if (section4AutoplayTimer !== null) {
        clearTimeout(section4AutoplayTimer);
      }
      const now = performance.now();
      const pauseRemaining = section4AutoplayResumeAt > now ? section4AutoplayResumeAt - now : 0;
      const nextDelay = pauseRemaining > 0 ? Math.max(180, pauseRemaining) : section4AutoplayIntervalMs;
      section4AutoplayTimer = window.setTimeout(() => {
        section4AutoplayTimer = null;
        const stillPaused = performance.now() < section4AutoplayResumeAt;
        const canAutoplayNow = !this.overlay && !this.legalOpen && !this.privacyOpen && this.currentSectionIndex === 3;
        if (!stillPaused && canAutoplayNow && this.section4Screens.length > 0) {
          const nextIndex = (section4ActiveIndex + 1) % this.section4Screens.length;
          scrollSection4ToIndex(nextIndex);
        }
        scheduleSection4Autoplay();
      }, nextDelay);
    };

    section4CarouselTrack.addEventListener('scroll', onSection4TrackScroll, { passive: true });
    section4CarouselTrack.addEventListener('mousedown', onSection4TrackMouseDown);
    section4CarouselTrack.addEventListener('mouseleave', endSection4MouseDrag);
    window.addEventListener('mousemove', onSection4TrackMouseMove);
    window.addEventListener('mouseup', endSection4MouseDrag);
    window.addEventListener('blur', endSection4MouseDrag);
    section4CarouselTrack.addEventListener('click', onSection4TrackClick);
    section4Slides.forEach((slide) => slide.addEventListener('dragstart', onSection4ImageDragStart));
    if (section4NavUp) { section4NavUp.addEventListener('click', onSection4NavUpClick as EventListener); }
    if (section4NavDown) { section4NavDown.addEventListener('click', onSection4NavDownClick as EventListener); }
    this.landingCleanup.push(() => section4CarouselTrack.removeEventListener('scroll', onSection4TrackScroll));
    this.landingCleanup.push(() => section4CarouselTrack.removeEventListener('mousedown', onSection4TrackMouseDown));
    this.landingCleanup.push(() => section4CarouselTrack.removeEventListener('mouseleave', endSection4MouseDrag));
    this.landingCleanup.push(() => window.removeEventListener('mousemove', onSection4TrackMouseMove));
    this.landingCleanup.push(() => window.removeEventListener('mouseup', endSection4MouseDrag));
    this.landingCleanup.push(() => window.removeEventListener('blur', endSection4MouseDrag));
    this.landingCleanup.push(() => section4CarouselTrack.removeEventListener('click', onSection4TrackClick));
    this.landingCleanup.push(() => section4Slides.forEach((slide) => slide.removeEventListener('dragstart', onSection4ImageDragStart)));
    this.landingCleanup.push(() => {
      if (section4NavUp) { section4NavUp.removeEventListener('click', onSection4NavUpClick as EventListener); }
      if (section4NavDown) { section4NavDown.removeEventListener('click', onSection4NavDownClick as EventListener); }
    });
    this.landingCleanup.push(() => {
      section4Carousel.classList.remove('is-dragging');
      setSection4Cursor('grab');
    });
    this.landingCleanup.push(() => {
      if (section4AutoplayTimer !== null) {
        clearTimeout(section4AutoplayTimer);
        section4AutoplayTimer = null;
      }
    });
    this.landingCleanup.push(() => {
      if (section4RebaseTimer !== null) {
        clearTimeout(section4RebaseTimer);
        section4RebaseTimer = null;
      }
      if (section4ReleaseSnapTimer !== null) {
        clearTimeout(section4ReleaseSnapTimer);
        section4ReleaseSnapTimer = null;
      }
    });
    this.landingCleanup.push(() => {
      if (section4ScrollRafId !== null) {
        cancelAnimationFrame(section4ScrollRafId);
        section4ScrollRafId = null;
      }
    });

    const clearSection2Timers = () => {
      if (section2WordTimer) { clearTimeout(section2WordTimer); section2WordTimer = null; }
      if (section2DetailTimer) { clearTimeout(section2DetailTimer); section2DetailTimer = null; }
      if (section2RestTimer) { clearTimeout(section2RestTimer); section2RestTimer = null; }
      if (section2RevealTimer) { clearTimeout(section2RevealTimer); section2RevealTimer = null; }
      if (section2IntroDelayTimer) { clearTimeout(section2IntroDelayTimer); section2IntroDelayTimer = null; }
      if (section2IntroDimTimer) { clearTimeout(section2IntroDimTimer); section2IntroDimTimer = null; }
      if (section2ExitTextTimer) { clearTimeout(section2ExitTextTimer); section2ExitTextTimer = null; }
      if (section2ExitBlurTimer) { clearTimeout(section2ExitBlurTimer); section2ExitBlurTimer = null; }
    };

    const clearSection3Timers = () => {
      if (section3WordTimer) { clearTimeout(section3WordTimer); section3WordTimer = null; }
      if (section3DetailTimer) { clearTimeout(section3DetailTimer); section3DetailTimer = null; }
      if (section3RestTimer) { clearTimeout(section3RestTimer); section3RestTimer = null; }
      if (section3IntroDelayTimer) { clearTimeout(section3IntroDelayTimer); section3IntroDelayTimer = null; }
      if (section3IntroDimTimer) { clearTimeout(section3IntroDimTimer); section3IntroDimTimer = null; }
      if (section3ExitTextTimer) { clearTimeout(section3ExitTextTimer); section3ExitTextTimer = null; }
      if (section3ReadyTimer) { clearTimeout(section3ReadyTimer); section3ReadyTimer = null; }
      if (section3NarrativeGateTimer) { clearTimeout(section3NarrativeGateTimer); section3NarrativeGateTimer = null; }
    };

    const runSection2TypingStep = () => {
      if (!section2NarrativeVisible) { return; }
      const currentWord = section2Words[section2WordIndex];
      const currentText = section2SwapWord.textContent || '';

      if (section2WordDeleting) {
        if (currentText.length > 0) {
          section2SwapWord.textContent = currentText.slice(0, -1);
          section2WordTimer = window.setTimeout(runSection2TypingStep, section2DeleteSpeed);
          return;
        }
        section2WordDeleting = false;
        section2WordIndex = (section2WordIndex + 1) % section2Words.length;
        section2WordTimer = window.setTimeout(runSection2TypingStep, section2TypePauseEmpty);
        return;
      }

      const nextLength = Math.min(currentText.length + 1, currentWord.length);
      section2SwapWord.textContent = currentWord.slice(0, nextLength);
      if (nextLength < currentWord.length) {
        section2WordTimer = window.setTimeout(runSection2TypingStep, section2TypeSpeed);
        return;
      }
      section2WordDeleting = true;
      section2WordTimer = window.setTimeout(runSection2TypingStep, section2TypePauseFull);
    };

    const showSection2Narrative = (withIntro = true, extraDelayMs = 0) => {
      if (section2NarrativeVisible || section2NarrativePending) { return; }
      section2NarrativeExiting = false;
      section2NarrativePending = true;
      clearSection2Timers();
      section2WordIndex = 0;
      section2WordDeleting = true;
      section2SwapWord.textContent = section2Words[section2WordIndex];
      narrativeDimForced = false;
      narrativeBlurForced = false;
      card.style.setProperty('--narrative-dim', '0');
      card.style.setProperty('--narrative-blur', '0');
      section2.style.setProperty('--s2-narrative-dim', '0');
      section2.style.setProperty('--s2-narrative-blur', '0');
      section2Narrative.classList.remove('is-visible', 'is-frame-visible', 'is-detail-visible', 'is-rest-visible');

      if (!withIntro) {
        narrativeDimForced = true;
        narrativeBlurForced = true;
        section2NarrativePending = false;
        section2NarrativeVisible = true;
        card.style.setProperty('--narrative-dim', '1');
        card.style.setProperty('--narrative-blur', '1');
        section2.style.setProperty('--s2-narrative-dim', '1');
        section2.style.setProperty('--s2-narrative-blur', '1');
        section2Narrative.classList.add('is-visible', 'is-frame-visible');
        section2DetailTimer = window.setTimeout(() => {
          section2Narrative.classList.add('is-detail-visible');
        }, sectionNarrativeDetailDelay);
        section2RestTimer = window.setTimeout(() => section2Narrative.classList.add('is-rest-visible'), section2NarrativeRestDelay);
        section2WordTimer = window.setTimeout(runSection2TypingStep, section2TypePauseFull);
        return;
      }

      section2IntroDelayTimer = window.setTimeout(() => {
        if (!section2NarrativePending) { return; }
        narrativeDimForced = true;
        narrativeBlurForced = true;
        card.style.setProperty('--narrative-dim', '1');
        card.style.setProperty('--narrative-blur', '1');
        section2.style.setProperty('--s2-narrative-dim', '1');
        section2.style.setProperty('--s2-narrative-blur', '1');

        section2IntroDimTimer = window.setTimeout(() => {
          if (!section2NarrativePending) { return; }
          section2NarrativePending = false;
          section2NarrativeVisible = true;
          section2Narrative.classList.add('is-visible', 'is-frame-visible');
          section2DetailTimer = window.setTimeout(() => {
            section2Narrative.classList.add('is-detail-visible');
          }, sectionNarrativeDetailDelay);
          section2RestTimer = window.setTimeout(() => section2Narrative.classList.add('is-rest-visible'), section2NarrativeRestDelay);
          section2WordTimer = window.setTimeout(runSection2TypingStep, section2TypePauseFull);
        }, section2NarrativeDimInDuration);
      }, extraDelayMs + section2NarrativePreDimDelay);
    };

    const startSection2NarrativeExit = () => {
      if (section2NarrativeExiting || (!section2NarrativeVisible && !section2NarrativePending)) { return; }
      section2NarrativeExiting = true;
      section2SuppressFx = true;
      section2NarrativeVisible = false;
      section2NarrativePending = false;
      clearSection2Timers();
      section2Narrative.classList.remove('is-visible', 'is-frame-visible', 'is-detail-visible', 'is-rest-visible');
      narrativeDimForced = false;
      narrativeBlurForced = false;
      card.style.setProperty('--narrative-dim', '0');
      card.style.setProperty('--narrative-blur', '0');
      section2.style.setProperty('--s2-narrative-dim', '0');
      section2.style.setProperty('--s2-narrative-blur', '0');
      section2ExitTextTimer = window.setTimeout(() => {
        section2ExitTextTimer = null;
      }, section2NarrativeTextFadeDuration);
    };

    const resetSection2Narrative = () => {
      if (!section2NarrativeVisible && !section2NarrativePending && !section2NarrativeExiting && !section2WordTimer && !section2DetailTimer) { return; }
      section2NarrativeVisible = false;
      section2NarrativePending = false;
      section2NarrativeExiting = false;
      section2SuppressFx = false;
      clearSection2Timers();
      narrativeDimForced = false;
      narrativeBlurForced = false;
      card.style.setProperty('--narrative-dim', '0');
      card.style.setProperty('--narrative-blur', '0');
      section2.style.setProperty('--s2-narrative-dim', '0');
      section2.style.setProperty('--s2-narrative-blur', '0');
      section2.style.setProperty('--s2-overlay-opacity', '1');
      section2Narrative.classList.remove('is-visible', 'is-frame-visible', 'is-detail-visible', 'is-rest-visible');
      section2WordIndex = 0;
      section2WordDeleting = false;
      section2SwapWord.textContent = section2Words[section2WordIndex];
    };

    const runSection3TypingStep = () => {
      if (!section3NarrativeVisible) { return; }
      const currentWord = section3Words[section3WordIndex];
      const currentText = section3SwapWord.textContent || '';

      if (section3WordDeleting) {
        if (currentText.length > 0) {
          section3SwapWord.textContent = currentText.slice(0, -1);
          section3WordTimer = window.setTimeout(runSection3TypingStep, section2DeleteSpeed);
          return;
        }
        section3WordDeleting = false;
        section3WordIndex = (section3WordIndex + 1) % section3Words.length;
        section3WordTimer = window.setTimeout(runSection3TypingStep, section2TypePauseEmpty);
        return;
      }

      const nextLength = Math.min(currentText.length + 1, currentWord.length);
      section3SwapWord.textContent = currentWord.slice(0, nextLength);
      if (nextLength < currentWord.length) {
        section3WordTimer = window.setTimeout(runSection3TypingStep, section2TypeSpeed);
        return;
      }
      section3WordDeleting = true;
      section3WordTimer = window.setTimeout(runSection3TypingStep, section2TypePauseFull);
    };

    const showSection3Narrative = (withIntro = true, extraDelayMs = 0) => {
      if (section3NarrativeVisible || section3NarrativePending) { return; }
      section3NarrativeExiting = false;
      section3NarrativePending = true;
      clearSection3Timers();
      section3WordIndex = 0;
      section3WordDeleting = true;
      section3SwapWord.textContent = section3Words[section3WordIndex];
      section3NarrativeDimForced = false;
      section3NarrativeBlurForced = false;
      section3Narrative.classList.remove('is-visible', 'is-frame-visible', 'is-detail-visible', 'is-rest-visible');

      if (!withIntro) {
        section3NarrativeDimForced = true;
        section3NarrativeBlurForced = true;
        section3NarrativePending = false;
        section3NarrativeVisible = true;
        section3Narrative.classList.add('is-visible', 'is-frame-visible');
        section3DetailTimer = window.setTimeout(() => {
          section3Narrative.classList.add('is-detail-visible');
        }, sectionNarrativeDetailDelay);
        section3RestTimer = window.setTimeout(() => section3Narrative.classList.add('is-rest-visible'), sectionNarrativeRestDelay);
        section3WordTimer = window.setTimeout(runSection3TypingStep, section2TypePauseFull);
        requestUpdate();
        return;
      }

      section3IntroDelayTimer = window.setTimeout(() => {
        if (!section3NarrativePending) { return; }
        section3NarrativeDimForced = true;
        section3NarrativeBlurForced = true;

        section3IntroDimTimer = window.setTimeout(() => {
          if (!section3NarrativePending) { return; }
          section3NarrativePending = false;
          section3NarrativeVisible = true;
          section3Narrative.classList.add('is-visible', 'is-frame-visible');
          section3DetailTimer = window.setTimeout(() => {
            section3Narrative.classList.add('is-detail-visible');
          }, sectionNarrativeDetailDelay);
          section3RestTimer = window.setTimeout(() => section3Narrative.classList.add('is-rest-visible'), sectionNarrativeRestDelay);
          section3WordTimer = window.setTimeout(runSection3TypingStep, section2TypePauseFull);
          requestUpdate();
        }, section2NarrativeDimInDuration);
      }, extraDelayMs + section2NarrativePreDimDelay);
    };

    const startSection3NarrativeExit = () => {
      if (section3NarrativeExiting || (!section3NarrativeVisible && !section3NarrativePending)) { return; }
      section3NarrativeExiting = true;
      section3SuppressFx = true;
      section3NarrativeVisible = false;
      section3NarrativePending = false;
      clearSection3Timers();
      section3Narrative.classList.remove('is-visible', 'is-frame-visible', 'is-detail-visible', 'is-rest-visible');
      section3NarrativeDimForced = false;
      section3NarrativeBlurForced = false;
      section3ExitTextTimer = window.setTimeout(() => {
        section3ExitTextTimer = null;
      }, section2NarrativeTextFadeDuration);
    };

    const resetSection3Narrative = () => {
      if (!section3NarrativeVisible && !section3NarrativePending && !section3NarrativeExiting && !section3WordTimer && !section3DetailTimer) { return; }
      section3NarrativeVisible = false;
      section3NarrativePending = false;
      section3NarrativeExiting = false;
      section3SuppressFx = false;
      clearSection3Timers();
      section3NarrativeDimForced = false;
      section3NarrativeBlurForced = false;
      section3Narrative.classList.remove('is-visible', 'is-frame-visible', 'is-detail-visible', 'is-rest-visible');
      section3WordIndex = 0;
      section3WordDeleting = false;
      section3SwapWord.textContent = section3Words[section3WordIndex];
    };

    this.loginLabelInterval = window.setInterval(rotateLoginLabel, 2000);
    this.landingCleanup.push(() => {
      if (this.loginLabelInterval) {
        clearInterval(this.loginLabelInterval);
        this.loginLabelInterval = null;
      }
    });
    const heroNextLabelInterval = window.setInterval(rotateHeroNextLabel, 1300);
    this.landingCleanup.push(() => clearInterval(heroNextLabelInterval));

    const doodleSectionIds = ['section-1', 'section-2', 'section-3', 'section-4'];
    interface DoodleLayer {
      section: HTMLElement;
      canvas: HTMLCanvasElement;
      ctx: CanvasRenderingContext2D;
      width: number;
      height: number;
      dpr: number;
      top: number;
      bottom: number;
      isPrimary: boolean;
    }
    interface DoodleItem {
      x: number;
      y: number;
      size: number;
      stroke: number;
      alpha: number;
      rotation: number;
      spinSpeed: number;
      primaryColor: string;
      life: number;
      ttl: number;
      type: string;
      driftX: number;
      driftY: number;
      wobbleAmp: number;
      wobbleFreq: number;
      phase: number;
      fadeIn: number;
      fadeOut: number;
      introDelay: number;
      introJitterX: number;
      introJitterY: number;
      introSwirl: number;
    }
    const doodleLayers: DoodleLayer[] = doodleSectionIds
      .map((id) => document.getElementById(id))
      .filter((section): section is HTMLElement => Boolean(section))
      .map((section) => {
        const isPrimary = section.id === 'section-1';
        const host = (isPrimary ? (section.querySelector('.section__inner') as HTMLElement | null) : section) || section;
        const canvas = document.createElement('canvas');
        canvas.className = 'section__doodles';
        if (!isPrimary) {
          canvas.classList.add('section__doodles--full');
        }
        canvas.setAttribute('aria-hidden', 'true');
        host.appendChild(canvas);
        const ctx = canvas.getContext('2d', { alpha: true });
        if (!ctx) {
          return null;
        }
        return { section, canvas, ctx, width: 0, height: 0, dpr: 1, top: 0, bottom: 0, isPrimary };
      })
      .filter((layer): layer is DoodleLayer => Boolean(layer));
    const doodles: DoodleItem[] = [];
    const doodleTypes = ['loop', 'wave', 'zigzag', 'spiral', 'arc', 'cross'];
    const doodleConfig = {
      count: 0,
      width: 0,
      height: 0,
      dpr: 1,
      rafId: null as number | null,
      lastTs: 0,
      playIntroBurst: false,
      introBurstStart: 0,
      introBurstDuration: 1900,
      introOriginYFactor: 0.6,
    };
    const rand = (min: number, max: number) => min + Math.random() * (max - min);
    const pick = <T, >(arr: T[]) => arr[Math.floor(Math.random() * arr.length)];
    const resetDoodle = (doodle: DoodleItem, width: number, height: number, allowAnywhere = false) => {
      const padding = 80;
      doodle.x = rand(allowAnywhere ? 0 : -padding, allowAnywhere ? width : width + padding);
      doodle.y = rand(allowAnywhere ? 0 : -padding, allowAnywhere ? height : height + padding);
      doodle.size = rand(20, 68);
      doodle.stroke = rand(1.8, 3.2);
      doodle.alpha = rand(0.45, 0.95);
      doodle.rotation = rand(0, Math.PI * 2);
      doodle.spinSpeed = rand(0.04, 0.16) * (Math.random() < 0.5 ? -1 : 1);
      doodle.primaryColor = Math.random() < 0.5 ? '255, 126, 216' : '112, 206, 255';
      doodle.life = 0;
      doodle.ttl = rand(6.5, 13.5);
      doodle.type = pick(doodleTypes);
      doodle.driftX = rand(-75, 75);
      doodle.driftY = rand(-60, 60);
      doodle.wobbleAmp = rand(8, 24);
      doodle.wobbleFreq = rand(0.55, 1.3);
      doodle.phase = rand(0, Math.PI * 2);
      doodle.fadeIn = rand(0.12, 0.26);
      doodle.fadeOut = rand(0.18, 0.34);
      doodle.introDelay = rand(0, 420);
      doodle.introJitterX = rand(-24, 24);
      doodle.introJitterY = rand(-16, 16);
      doodle.introSwirl = rand(-16, 16);
    };
    const rebuildDoodles = () => {
      doodles.length = 0;
      for (let i = 0; i < doodleConfig.count; i += 1) {
        const doodle = {} as DoodleItem;
        resetDoodle(doodle, doodleConfig.width, doodleConfig.height, true);
        doodle.life = Math.random() * doodle.ttl;
        doodles.push(doodle);
      }
    };
    const drawDoodleShape = (ctx: CanvasRenderingContext2D, type: string, size: number) => {
      const h = size * 0.5;
      switch (type) {
        case 'loop':
          ctx.ellipse(0, 0, size * 0.45, h * 0.8, 0.3, 0, Math.PI * 2);
          break;
        case 'wave':
          ctx.moveTo(-size * 0.5, 0);
          ctx.bezierCurveTo(-size * 0.25, -h, 0, h, size * 0.25, 0);
          ctx.bezierCurveTo(size * 0.35, -h * 0.9, size * 0.5, h * 0.55, size * 0.55, -h * 0.1);
          break;
        case 'zigzag':
          ctx.moveTo(-size * 0.55, -h * 0.25);
          ctx.lineTo(-size * 0.2, h * 0.35);
          ctx.lineTo(size * 0.08, -h * 0.28);
          ctx.lineTo(size * 0.38, h * 0.34);
          break;
        case 'spiral':
          ctx.moveTo(0, 0);
          ctx.bezierCurveTo(size * 0.3, -h * 0.2, size * 0.4, h * 0.35, size * 0.15, h * 0.5);
          ctx.bezierCurveTo(-size * 0.22, h * 0.58, -size * 0.42, h * 0.08, -size * 0.2, -h * 0.28);
          ctx.bezierCurveTo(size * 0.06, -h * 0.62, size * 0.3, -h * 0.35, size * 0.18, -h * 0.02);
          break;
        case 'arc':
          ctx.arc(0, 0, size * 0.5, Math.PI * 0.12, Math.PI * 1.08);
          break;
        case 'cross':
          ctx.moveTo(-size * 0.45, 0);
          ctx.lineTo(size * 0.45, 0);
          ctx.moveTo(0, -h * 0.9);
          ctx.lineTo(0, h * 0.9);
          break;
        default:
          ctx.moveTo(-size * 0.5, 0);
          ctx.lineTo(size * 0.5, 0);
      }
    };
    const drawDoodles = (deltaSec: number) => {
      const visibleLayers = prefersReducedMotion.matches
        ? doodleLayers
        : doodleLayers.filter((layer) => layer.bottom > window.scrollY && layer.top < window.scrollY + window.innerHeight);
      if (visibleLayers.length === 0) { return; }
      const primaryLayer = doodleLayers.find((layer) => layer.isPrimary) || doodleLayers[0];
      const { width, height } = primaryLayer;
      if (width < 1 || height < 1) { return; }
      const introActive = doodleConfig.playIntroBurst && doodleConfig.introBurstStart > 0;
      const introElapsed = introActive ? performance.now() - doodleConfig.introBurstStart : 0;
      visibleLayers.forEach((layer) => layer.ctx.clearRect(0, 0, layer.width, layer.height));

      doodles.forEach((doodle) => {
        doodle.life += deltaSec;
        if (doodle.life >= doodle.ttl) {
          resetDoodle(doodle, width, height);
        }

        const progress = doodle.life / doodle.ttl;
        const fadeInRatio = Math.min(1, progress / doodle.fadeIn);
        const fadeOutStart = 1 - doodle.fadeOut;
        const fadeOutRatio = progress > fadeOutStart ? 1 - (progress - fadeOutStart) / doodle.fadeOut : 1;
        let visible = Math.max(0, Math.min(1, fadeInRatio * fadeOutRatio));
        if (visible <= 0.01) { return; }

        let px = doodle.x + doodle.driftX * progress + Math.sin(progress * Math.PI * 2 * doodle.wobbleFreq + doodle.phase) * doodle.wobbleAmp;
        let py = doodle.y + doodle.driftY * progress + Math.cos(progress * Math.PI * 2 * doodle.wobbleFreq + doodle.phase) * (doodle.wobbleAmp * 0.58);

        if (introActive) {
          const localIntro = clamp((introElapsed - doodle.introDelay) / (doodleConfig.introBurstDuration - doodle.introDelay * 0.38), 0, 1);
          const introEase = 1 - Math.pow(1 - localIntro, 3);
          const originX = width * 0.5 + doodle.introJitterX;
          const originY = height * doodleConfig.introOriginYFactor + doodle.introJitterY;
          const swirlX = Math.sin((localIntro + doodle.phase) * Math.PI * 2) * doodle.introSwirl * (1 - localIntro);
          const swirlY = Math.cos((localIntro + doodle.phase * 0.7) * Math.PI * 2) * doodle.introSwirl * 0.55 * (1 - localIntro);
          px = originX + (px - originX) * introEase + swirlX;
          py = originY + (py - originY) * introEase + swirlY;
          const introOpacity = localIntro < 0.16 ? localIntro / 0.16 : 1;
          visible *= introOpacity;
        }

        visibleLayers.forEach((layer) => {
          const scaleX = layer.width / width;
          const scaleY = layer.height / height;
          const scale = (scaleX + scaleY) * 0.5;
          const x = px * scaleX;
          const y = py * scaleY;

          layer.ctx.save();
          layer.ctx.translate(x, y);
          layer.ctx.rotate(doodle.rotation + doodle.life * doodle.spinSpeed);
          layer.ctx.beginPath();
          drawDoodleShape(layer.ctx, doodle.type, doodle.size * scale);
          const colorRgb = layer.isPrimary ? doodle.primaryColor : '255, 255, 255';
          const layerOpacity = layer.isPrimary ? 1 : 0.55;
          const strokeAlpha = doodle.alpha * visible * layerOpacity;
          layer.ctx.strokeStyle = `rgba(${colorRgb}, ${strokeAlpha.toFixed(3)})`;
          layer.ctx.lineWidth = doodle.stroke * scale;
          layer.ctx.lineCap = 'round';
          layer.ctx.lineJoin = 'round';
          layer.ctx.shadowColor = layer.isPrimary ? `rgba(${colorRgb}, 0.55)` : 'rgba(255, 255, 255, 0.24)';
          layer.ctx.shadowBlur = 6 * scale;
          layer.ctx.stroke();
          layer.ctx.restore();
        });
      });

      if (introActive && introElapsed > doodleConfig.introBurstDuration + 460) {
        doodleConfig.playIntroBurst = false;
      }
    };
    const resizeDoodleCanvas = () => {
      if (doodleLayers.length === 0) { return; }
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      doodleLayers.forEach((layer) => {
        layer.top = layer.section.offsetTop;
        layer.bottom = layer.top + layer.section.offsetHeight;
        const rect = layer.canvas.parentElement?.getBoundingClientRect();
        if (!rect) { return; }
        layer.width = Math.max(1, Math.round(rect.width));
        layer.height = Math.max(1, Math.round(rect.height));
        layer.dpr = dpr;
        layer.canvas.width = Math.round(layer.width * layer.dpr);
        layer.canvas.height = Math.round(layer.height * layer.dpr);
        layer.canvas.style.width = `${layer.width}px`;
        layer.canvas.style.height = `${layer.height}px`;
        layer.ctx.setTransform(layer.dpr, 0, 0, layer.dpr, 0, 0);
      });
      const primaryLayer = doodleLayers.find((layer) => layer.isPrimary) || doodleLayers[0];
      doodleConfig.width = primaryLayer.width;
      doodleConfig.height = primaryLayer.height;
      doodleConfig.dpr = primaryLayer.dpr;
      doodleConfig.count = Math.max(10, Math.min(28, Math.round((doodleConfig.width * doodleConfig.height) / 62000)));
      rebuildDoodles();
      drawDoodles(0);
    };
    const doodleFrame = (ts: number) => {
      if (!doodleConfig.lastTs) { doodleConfig.lastTs = ts; }
      const deltaSec = Math.min((ts - doodleConfig.lastTs) / 1000, 0.05);
      doodleConfig.lastTs = ts;
      drawDoodles(deltaSec);
      doodleConfig.rafId = requestAnimationFrame(doodleFrame);
    };
    const setDoodleMotion = () => {
      if (prefersReducedMotion.matches) {
        if (doodleConfig.rafId) {
          cancelAnimationFrame(doodleConfig.rafId);
          doodleConfig.rafId = null;
        }
        doodleConfig.lastTs = 0;
        drawDoodles(0);
        return;
      }
      if (!doodleConfig.rafId) {
        doodleConfig.lastTs = 0;
        doodleConfig.rafId = requestAnimationFrame(doodleFrame);
      }
    };
    this.landingCleanup.push(() => {
      if (doodleConfig.rafId !== null) {
        cancelAnimationFrame(doodleConfig.rafId);
        doodleConfig.rafId = null;
      }
      doodleLayers.forEach(({ canvas }) => canvas.remove());
    });
    const startDoodleIntroBurst = () => {
      doodleConfig.playIntroBurst = true;
      doodleConfig.introBurstStart = performance.now();
    };
    const runIntroSequence = () => {
      if (introSequenceStarted) { return; }
      introSequenceStarted = true;
      if (!bodyEl || !heroIntroSplash) {
        startDoodleIntroBurst();
        return;
      }

      const splashHoldMs = prefersReducedMotion.matches ? 420 : 2500;
      const splashFadeMs = prefersReducedMotion.matches ? 160 : 300;
      const splashGoneMs = splashHoldMs + splashFadeMs;
      const decoratorInStartMs = splashGoneMs + (prefersReducedMotion.matches ? 40 : 120);
      const decoratorDropDurationMs = prefersReducedMotion.matches ? 320 : 760;
      const burstTailMs = prefersReducedMotion.matches ? 660 : 730;
      const burstStartMs = Math.max(splashGoneMs, decoratorInStartMs + decoratorDropDurationMs - burstTailMs);
      const uiRevealAfterBurstMs = prefersReducedMotion.matches ? 260 : 1020;
      const uiRevealStartMs = decoratorInStartMs + (prefersReducedMotion.matches ? 90 : 300);

      bodyEl.classList.add('intro-sequence', 'intro-phase--splash');
      bodyEl.classList.remove('intro-phase--logo-out', 'intro-phase--burst', 'intro-phase--decorator-in', 'intro-phase--ui-reveal');
      bodyEl.classList.remove('login-attention-ready');

      this.introTimers.push(window.setTimeout(() => {
        bodyEl.classList.add('intro-phase--logo-out');
      }, splashHoldMs));

      this.introTimers.push(window.setTimeout(() => {
        bodyEl.classList.add('intro-phase--burst');
        startDoodleIntroBurst();
      }, burstStartMs));

      this.introTimers.push(window.setTimeout(() => {
        bodyEl.classList.remove('intro-phase--splash', 'intro-phase--logo-out');
      }, splashHoldMs + splashFadeMs));

      this.introTimers.push(window.setTimeout(() => {
        bodyEl.classList.add('intro-phase--decorator-in');
      }, decoratorInStartMs));

      this.introTimers.push(window.setTimeout(() => {
        this.useDarkHeroDecorator = true;
      }, decoratorInStartMs + decoratorDropDurationMs + 1500));

      this.introTimers.push(window.setTimeout(() => {
        bodyEl.classList.add('intro-phase--ui-reveal');
        bodyEl.classList.add('login-attention-ready');
      }, uiRevealStartMs));

      this.introTimers.push(window.setTimeout(() => {
        bodyEl.classList.remove('intro-sequence', 'intro-phase--burst', 'intro-phase--decorator-in', 'intro-phase--ui-reveal');
        this.heroNextCtaVisible = true;
      }, splashHoldMs + splashFadeMs + uiRevealAfterBurstMs));
    };

    const update = () => {
      const vh = window.innerHeight;
      const s2 = section2.getBoundingClientRect();
      const s3 = section3.getBoundingClientRect();
      const s4 = section4.getBoundingClientRect();
      const s5 = section5.getBoundingClientRect();
      const s6 = section6.getBoundingClientRect();

      const section6ApproachRaw = prefersReducedMotion.matches ? 0 : clamp((vh - s6.top) / (vh * 0.82), 0, 1);
      const section6Approach = section6ApproachRaw * section6ApproachRaw * (3 - 2 * section6ApproachRaw);
      section5.style.setProperty('--s6-approach', section6Approach.toFixed(3));
      section6.style.setProperty('--s6-approach', section6Approach.toFixed(3));
      if (s6.top <= 12 && s6.bottom > vh * 0.5) {
        communityFireworks.start();
      } else if (s6.top >= vh - 8) {
        communityFireworks.reset();
      }
      section5.style.setProperty('--s5-entry-shade', clamp(s5.top / (vh * 0.3), 0, 1).toFixed(3));
      const section5Entry = clamp((vh * 0.92 - s5.top) / (vh * 0.78), 0, 1);
      section5.style.setProperty('--s5-entry-progress', (prefersReducedMotion.matches ? 1 : section5Entry * section5Entry * (3 - 2 * section5Entry)).toFixed(3));

      const progress = clamp((vh - s2.top) / vh, 0, 1);
      hero?.style.setProperty('--hero-scroll-dim', clamp((progress - 0.04) / 0.58, 0, 1).toFixed(3));
      hero?.style.setProperty('--hero-return-progress', clamp((s2.top / vh - 0.08) / 0.72, 0, 1).toFixed(3));
      const section2ShadowEntry = clamp((progress - 0.02) / 0.12, 0, 1);
      const section2ShadowFade = 1 - clamp((progress - 0.16) / 0.58, 0, 1);
      const section2EntryShadow = section2ShadowEntry * section2ShadowFade;
      const eased = 1 - Math.pow(1 - progress, 3);
      const entranceProgress = clamp((progress - 0.08) / 0.74, 0, 1);
      const entranceEase = 1 - Math.pow(1 - entranceProgress, 3);
      const translateY = 84 - 84 * entranceEase;
      const rotate = -14 + 14 * eased;
      const tiltX = 22 - 22 * entranceEase;
      const tiltY = -12 + 12 * entranceEase;
      const swapProgress = clamp((progress - 0.82) / 0.12, 0, 1);
      const scale = 1 + 0.26 * swapProgress;
      const fadeIn = clamp((progress - 0.08) / 0.18, 0, 1);

      const exitProgress = clamp((vh - s6.top) / (vh * 0.85), 0, 1);
      const exitEase = 1 - Math.pow(1 - exitProgress, 3);
      const exitY = -80 * exitEase;
      const section3EntryProgress = clamp((vh - s3.top) / (vh * 0.88), 0, 1);
      const s3FlipProgress = clamp((section3EntryProgress - 0.2) / 0.8, 0, 1);
      const narrativeFade = clamp((section3EntryProgress - 0.06) / 0.44, 0, 1);
      const section4EntryProgress = clamp((vh - s4.top) / (vh * 0.88), 0, 1);
      const section4DistanceToTop = section4.offsetTop - window.scrollY;
      const section4GrowRaw = clamp(1 - (section4DistanceToTop / (vh * 0.6)), 0, 1);
      const section4GrowProgress = 1 - Math.pow(1 - section4GrowRaw, 2.4);
      const section4CarouselProgress = clamp(1 - section4DistanceToTop / (vh * 0.55), 0, 1);
      const section3FoldProgress = clamp((vh * 0.9 - section4DistanceToTop) / (vh * 0.35), 0, 1);
      const section3ExitFade = clamp((section3FoldProgress - 0.4) / 0.6, 0, 1);
      const section3NarrativeFade = clamp((section4EntryProgress - 0.06) / 0.44, 0, 1);
      section3Narrative.classList.toggle('is-scrolling-to-section4', section4EntryProgress > 0.06);
      const section3SyncFade = 1 - section3NarrativeFade;
      const sceneOpacity = (s2.top < vh ? fadeIn : 0) * (1 - exitProgress) * section3SyncFade;
      const cardOpacity = s3FlipProgress >= 0.5
        ? 1 - section3ExitFade
        : sceneOpacity;
      const inSection2NarrativeRange = window.scrollY >= section2.offsetTop - 8 && window.scrollY < section3.offsetTop - 8;
      const inSection3NarrativeRange = window.scrollY >= section3.offsetTop - 8 && window.scrollY < section4.offsetTop - 8;
      const section3BackFaceVisible = s3FlipProgress >= 0.5 && section3ExitFade < 1;
      const section3RevealStarted = s3FlipProgress >= 0.56 && section3ExitFade < 1;
      // Determine active section from absolute scroll position.
      // This is more stable than center-of-rect when sticky/overlayed sections are present.
      const sectionProbeY = window.scrollY + (vh * 0.45);
      let nearestSectionIndex = 0;
      sectionNodes.forEach((sectionNode, idx) => {
        if (sectionNode.offsetTop <= sectionProbeY) {
          nearestSectionIndex = idx;
        }
      });
      if (nearestSectionIndex !== this.currentSectionIndex) {
        this.currentSectionIndex = nearestSectionIndex;
      }

      if (section6Panel && section6BottomIcon && s6.top < vh && s6.bottom > 0) {
        const panelRect = section6Panel.getBoundingClientRect();
        const iconRect = section6BottomIcon.getBoundingClientRect();
        const desiredGap = 10;
        const availableGap = iconRect.top - panelRect.bottom;
        const overlap = Math.max(0, desiredGap - availableGap);
        const iconHeight = Math.max(1, iconRect.height);
        const overlapRatio = overlap / iconHeight;
        const iconScale = clamp(1 - overlapRatio, 0.56, 1);
        const iconShift = overlap > 0 ? overlap + 2 : 0;
        section6.style.setProperty('--s6-bottom-icon-scale', iconScale.toFixed(3));
        section6.style.setProperty('--s6-bottom-icon-shift', `${iconShift.toFixed(1)}px`);
      }

      card.style.opacity = String(cardOpacity);
      card.style.setProperty('--swap-progress', swapProgress.toFixed(3));
      card.style.setProperty('--s3-flip', (prefersReducedMotion.matches ? Math.round(s3FlipProgress) : s3FlipProgress).toFixed(3));
      card.style.setProperty('--s3-front-fade', (s3FlipProgress >= 0.5 ? 1 : 0).toString());
      card.style.setProperty('--s3-close-progress', section3ExitFade.toFixed(3));
      card.style.setProperty('--s3-exit-fold', (prefersReducedMotion.matches ? 0 : section3FoldProgress).toFixed(3));
      const cardHalfVh = ((card.offsetHeight || 0) * 0.5 / vh) * 100;
      const section3To4LiftTargetVh = 14 + cardHalfVh * 0.18;
      const section3To4LiftY = -section3To4LiftTargetVh * section4GrowProgress;
      const section3To4CloseX = 0;
      const section3To4TiltX = section3To4CloseX;
      const section3To4TiltY = 0;
      const section3To4Rotate = 0;
      const section3To4Scale = 1 - 0.045 * section4GrowProgress;
      const section3To4Depth = -24 * section4GrowProgress;
      const section3To4CardTranslateY = translateY + exitY + section3To4LiftY;
      const section3To4CardRotateX = tiltX + section3To4TiltX;
      card.style.transform = `perspective(900px) translate(-50%, -50%) translateY(${section3To4CardTranslateY}vh) translateZ(${section3To4Depth}px) rotateX(${section3To4CardRotateX}deg) rotateY(${tiltY + section3To4TiltY}deg) rotate(${rotate + section3To4Rotate}deg) scale(${scale * section3To4Scale})`;
      section4Carousel.style.setProperty('--s4-grow', section4CarouselProgress.toFixed(3));
      section4Carousel.style.setProperty('--s4-grow-inv', (1 - section4CarouselProgress).toFixed(3));
      section4Carousel.style.setProperty('--s4-lift', `${-clamp(s4.top, 0, vh).toFixed(1)}px`);
      section4Carousel.classList.toggle('is-arriving', s4.top < vh * 0.55 && s4.top > -vh * 0.2);
      section4Carousel.classList.toggle('is-transitioning', section4CarouselProgress < 0.8);
      section4CarouselTrack.style.pointerEvents = section4CarouselProgress < 0.8 ? 'none' : 'auto';

      const canRenderSection2Fx = inSection2NarrativeRange && !section2SuppressFx;
      const narrativeDimStrength = canRenderSection2Fx && (section2NarrativeVisible || narrativeDimForced || section2NarrativeExiting)
        ? (section2NarrativeExiting ? 1 : 1 - narrativeFade)
        : 0;
      const narrativeBlurStrength = canRenderSection2Fx && (section2NarrativeVisible || narrativeBlurForced) ? 1 - narrativeFade : 0;
      // Keep card content readable while entering section 2; dim/blur starts later.
      const section2DimByProgress = canRenderSection2Fx ? clamp((swapProgress - 0.9) / 0.1, 0, 1) * 0.1 : 0;
      const section2BlurByProgress = canRenderSection2Fx ? clamp((swapProgress - 0.95) / 0.05, 0, 1) * 0.08 : 0;
      const canRenderSection3Fx = inSection3NarrativeRange && !section3SuppressFx;
      const section3FxReady = section3DevsSwapped || section3NarrativeExiting;
      const section3BackdropRampRaw = clamp((section3EntryProgress - 0.64) / 0.34, 0, 1);
      const section3BackdropRamp = Math.pow(section3BackdropRampRaw, 2.1);
      const section3PreludeDim = section3RevealStarted ? section3BackdropRamp * 0.28 : 0;
      const section3NarrativeDimStrength = canRenderSection3Fx && section3FxReady && (section3NarrativeVisible || section3NarrativeDimForced || section3NarrativeExiting)
        ? (section3NarrativeExiting ? 1 : (1 - section3NarrativeFade) * section3BackdropRamp)
        : canRenderSection3Fx ? section3PreludeDim : 0;
      const section3NarrativeBlurStrength = canRenderSection3Fx && section3FxReady && (section3NarrativeVisible || section3NarrativeBlurForced)
        ? (1 - section3NarrativeFade) * section3BackdropRamp
        : 0;
      const section3ExitLight = 1 - section3FoldProgress;
      const section3BackdropStrength = Math.max(section3NarrativeDimStrength, section3NarrativeBlurStrength) * section3ExitLight;
      const finalDim = Math.max(section2DimByProgress, narrativeDimStrength, section3NarrativeDimStrength * section3ExitLight);
      const finalBlur = Math.max(section2BlurByProgress, narrativeBlurStrength, section3NarrativeBlurStrength * section3ExitLight);

      card.style.setProperty('--narrative-dim', finalDim.toFixed(3));
      card.style.setProperty('--narrative-blur', finalBlur.toFixed(3));
      card.style.setProperty('--section3-card-overlay', section3BackdropStrength.toFixed(3));
      section2Narrative.style.setProperty('--scene-opacity', sceneOpacity.toFixed(3));
      section2Narrative.style.setProperty('--section3-fade', narrativeFade.toFixed(3));
      section3Narrative.style.setProperty('--scene-opacity', sceneOpacity.toFixed(3));
      section3Narrative.style.setProperty('--section3-fade', section3NarrativeFade.toFixed(3));
      section2.style.setProperty('--s2-overlay-opacity', '1');
      section2.style.setProperty('--s2-entry-shadow', section2EntryShadow.toFixed(3));
      section2.style.setProperty('--s2-narrative-dim', finalDim.toFixed(3));
      section2.style.setProperty('--s2-narrative-blur', finalBlur.toFixed(3));
      const nextGlobalDimOpacity = finalDim * 0.72;
      const nextGlobalBlurOpacity = finalBlur * 0.78;
      section2GlobalDimLayer?.classList.toggle('is-section3-transition', inSection3NarrativeRange);
      section2GlobalBlurLayer?.classList.toggle('is-section3-transition', inSection3NarrativeRange);
      if (section2GlobalDimLayer && Math.abs(nextGlobalDimOpacity - lastGlobalDimOpacity) > 0.001) {
        section2GlobalDimLayer.style.opacity = nextGlobalDimOpacity.toFixed(3);
        lastGlobalDimOpacity = nextGlobalDimOpacity;
      }
      if (section2GlobalBlurLayer && Math.abs(nextGlobalBlurOpacity - lastGlobalBlurOpacity) > 0.001) {
        section2GlobalBlurLayer.style.opacity = nextGlobalBlurOpacity.toFixed(3);
        lastGlobalBlurOpacity = nextGlobalBlurOpacity;
      }

      const shouldShowSection2Narrative =
        swapProgress >= section2NarrativeRevealSwapThreshold &&
        sceneOpacity > section2NarrativeRevealOpacityThreshold &&
        narrativeFade < 0.9 &&
        inSection2NarrativeRange &&
        !section2SuppressFx &&
        !section2NarrativeExiting &&
        section2To3Timer === null;
      if (shouldShowSection2Narrative && !section2NarrativeVisible) {
        const fromSection1To2 = section3EntryProgress < 0.02;
        showSection2Narrative(fromSection1To2, fromSection1To2 ? section2NarrativeHeroHoldDelay : 0);
      }
      if (!inSection2NarrativeRange && (section2NarrativeVisible || section2NarrativePending || section2NarrativeExiting)) {
        resetSection2Narrative();
      }
      if (swapProgress < 0.2 || sceneOpacity < 0.16) {
        resetSection2Narrative();
      }
      if (narrativeFade > 0.98) {
        startSection2NarrativeExit();
      }

      const shouldShowSection3Narrative =
        section3DevsSwapped &&
        section3NarrativeReadyAfterDev &&
        section3NarrativeFade < 0.98 &&
        inSection3NarrativeRange &&
        !section3SuppressFx &&
        !section3NarrativeExiting &&
        section3To4Timer === null;
      if (shouldShowSection3Narrative && !section3NarrativeVisible) {
        showSection3Narrative(true, section3NarrativeRevealHoldDelay);
      }
      if (!inSection3NarrativeRange && (section3NarrativeVisible || section3NarrativePending || section3NarrativeExiting)) {
        resetSection3Narrative();
      }
      if (section3NarrativeFade > 0.98) {
        startSection3NarrativeExit();
      }

      if (section3BackFaceVisible) {
        section3DevsSwapped = true;
        card.style.setProperty('--s3-devs-fade', section3RevealStarted ? '1' : '0');
      } else {
        section3DevsSwapped = false;
        card.style.setProperty('--s3-devs-fade', '0');
      }

      const shouldArmSection3Narrative = section3BackFaceVisible && section3DevsSwapped && s3FlipProgress >= 0.99;
      if (shouldArmSection3Narrative) {
        if (!section3ReadyAfterFlipDelay && section3ReadyTimer === null) {
          section3ReadyTimer = window.setTimeout(() => {
            section3ReadyTimer = null;
            section3ReadyAfterFlipDelay = true;
            section3NarrativeReadyAfterDev = false;
            if (section3NarrativeGateTimer !== null) {
              clearTimeout(section3NarrativeGateTimer);
            }
            section3NarrativeGateTimer = window.setTimeout(() => {
              section3NarrativeGateTimer = null;
              section3NarrativeReadyAfterDev = true;
              requestUpdate();
            }, section3NarrativeAfterDevDelay);
            requestUpdate();
          }, section3DevSwapStartDelay);
        }
        } else {
        const shouldResetSection3Sequence = section3NarrativeFade >= 0.98 || s3FlipProgress < 0.5;
        if (shouldResetSection3Sequence) {
          if (section3ReadyTimer !== null) {
            clearTimeout(section3ReadyTimer);
            section3ReadyTimer = null;
          }
          if (section3NarrativeGateTimer !== null) {
            clearTimeout(section3NarrativeGateTimer);
            section3NarrativeGateTimer = null;
          }
          section3ReadyAfterFlipDelay = false;
          section3NarrativeReadyAfterDev = false;
        }
      }

      const justCrossedSwapThreshold = prevSwapProgress < section2NarrativeRevealSwapThreshold &&
        swapProgress >= section2NarrativeRevealSwapThreshold;
      if (justCrossedSwapThreshold && !shimmerTriggered && sceneOpacity > 0.65) {
        shimmerTriggered = true;
        card.classList.remove('is-shimmering');
        void card.offsetWidth;
        card.classList.add('is-shimmering');
      } else if (swapProgress < 0.2) {
        shimmerTriggered = false;
      }

      card.classList.toggle('is-section3-clean', s3FlipProgress >= 0.5);

      lastSwap = swapProgress;
      prevSwapProgress = swapProgress;
      lastCardOpacity = sceneOpacity;
      liveSwapProgress = swapProgress;
      liveSection3EntryProgress = section3EntryProgress;
      updateLoginTone();
    };

    const requestUpdate = () => {
      if (this.landingRafId) { return; }
      this.landingRafId = requestAnimationFrame(() => {
        this.landingRafId = null;
        update();
      });
    };

    const cancelSnap = () => {
      snapTargetY = null;
      if (snapRafId !== null) {
        cancelAnimationFrame(snapRafId);
        snapRafId = null;
      }
      if (scrollEndTimer !== null) {
        clearTimeout(scrollEndTimer);
        scrollEndTimer = null;
      }
    };
    this.landingCancelSnap = cancelSnap;
    this.landingCleanup.push(() => { this.landingCancelSnap = null; });
    const scheduleSettle = (delay = 90) => {
      if (scrollEndTimer !== null) { clearTimeout(scrollEndTimer); }
      scrollEndTimer = window.setTimeout(settleAfterScroll, delay);
    };
    const onUserScrollIntent = (event: Event) => {
      if (event instanceof WheelEvent && event.deltaY) {
        scrollDirection = Math.sign(event.deltaY);
        lastWheelAt = performance.now();
      }
      this.manualSectionNavUntil = 0;
      this.cancelDotNavigation();
      cancelSnap();
      if (!gestureActive) { scheduleSettle(); }
    };
    const onGestureStart = () => {
      gestureActive = true;
      this.manualSectionNavUntil = 0;
      this.cancelDotNavigation();
      cancelSnap();
    };
    const onGestureEnd = () => {
      gestureActive = false;
      scheduleSettle();
    };
    const onScroll = () => {
      const y = window.scrollY;
      if (this.overlay || this.legalOpen || this.privacyOpen) {
        lastScrollY = y;
        return;
      }
      if (snapTargetY === null && performance.now() - lastWheelAt > 250 && Math.abs(y - lastScrollY) > 0.3) {
        scrollDirection = y > lastScrollY ? 1 : -1;
      }
      lastScrollY = y;
      requestUpdate();
      if (performance.now() < this.manualSectionNavUntil) {
        const remaining = this.manualSectionNavUntil - performance.now();
        cancelSnap();
        scheduleSettle(remaining + 10);
        return;
      }
      if (gestureActive) { return; }
      // A completed snap must not trigger another snap from its own scroll event.
      if (snapTargetY !== null && (snapRafId !== null || Math.abs(y - snapTargetY) < 2)) { return; }
      scheduleSettle();
    };
    const settleAfterScroll = () => {
      scrollEndTimer = null;
      if (this.overlay || this.legalOpen || this.privacyOpen || snapRafId !== null || performance.now() < this.manualSectionNavUntil) { return; }
      const y = window.scrollY;
      const nextIndex = sectionNodes.findIndex((section) => section.offsetTop > y);
      if (nextIndex <= 0) { return; }
      const previousSection = sectionNodes[nextIndex - 1];
      const nextSection = sectionNodes[nextIndex];
      if (Math.abs(previousSection.offsetTop - y) < 2) { return; }
      const target = scrollDirection > 0 ? nextSection : scrollDirection < 0 ? previousSection : null;
      if (!target || Math.abs(target.offsetTop - y) < 2) { return; }
      snapTargetY = target.offsetTop;
      if (prefersReducedMotion.matches) {
        window.scrollTo(0, target.offsetTop);
        return;
      }
      const startY = window.scrollY;
      const distance = target.offsetTop - startY;
      const startedAt = performance.now();
      const duration = Math.min(470, Math.max(320, Math.abs(distance) * 0.6));
      const animate = (now: number) => {
        const progress = Math.min(1, (now - startedAt) / duration);
        window.scrollTo(0, startY + distance * progress * progress);
        snapRafId = progress < 1 ? requestAnimationFrame(animate) : null;
      };
      snapRafId = requestAnimationFrame(animate);
    };

    this.landingScrollHandler = onScroll;
    this.landingResizeHandler = () => {
      requestUpdate();
      resizeDoodleCanvas();
      updateSection4ActiveSlide();
      scheduleSection4Autoplay();
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('wheel', onUserScrollIntent, { passive: true });
    window.addEventListener('touchstart', onGestureStart, { passive: true });
    window.addEventListener('touchend', onGestureEnd, { passive: true });
    window.addEventListener('touchcancel', onGestureEnd, { passive: true });
    window.addEventListener('pointerdown', onGestureStart, { passive: true });
    window.addEventListener('pointerup', onGestureEnd, { passive: true });
    window.addEventListener('pointercancel', onGestureEnd, { passive: true });
    window.addEventListener('keydown', onUserScrollIntent);
    window.addEventListener('resize', this.landingResizeHandler);
    this.landingCleanup.push(() => window.removeEventListener('scroll', onScroll));
    this.landingCleanup.push(() => window.removeEventListener('wheel', onUserScrollIntent));
    this.landingCleanup.push(() => window.removeEventListener('touchstart', onGestureStart));
    this.landingCleanup.push(() => window.removeEventListener('touchend', onGestureEnd));
    this.landingCleanup.push(() => window.removeEventListener('touchcancel', onGestureEnd));
    this.landingCleanup.push(() => window.removeEventListener('pointerdown', onGestureStart));
    this.landingCleanup.push(() => window.removeEventListener('pointerup', onGestureEnd));
    this.landingCleanup.push(() => window.removeEventListener('pointercancel', onGestureEnd));
    this.landingCleanup.push(() => window.removeEventListener('keydown', onUserScrollIntent));
    this.landingCleanup.push(cancelSnap);
    this.landingCleanup.push(() => {
      if (this.landingResizeHandler) {
        window.removeEventListener('resize', this.landingResizeHandler);
      }
    });
    this.landingCleanup.push(() => {
      if (section2To3Timer) {
        clearTimeout(section2To3Timer);
        section2To3Timer = null;
      }
      if (section3To4Timer) {
        clearTimeout(section3To4Timer);
        section3To4Timer = null;
      }
    });

    const onShimmerAnimationEnd = () => {
      card.classList.remove('is-shimmering');
      if (section2RevealTimer) {
        clearTimeout(section2RevealTimer);
        section2RevealTimer = null;
      }
      const inSection2NarrativeRange = window.scrollY >= section2.offsetTop - 8 && window.scrollY < section3.offsetTop - 8;
      if (
        liveSwapProgress >= section2NarrativeRevealSwapThreshold &&
        lastCardOpacity > section2NarrativeRevealOpacityThreshold &&
        inSection2NarrativeRange &&
        !section2NarrativeExiting &&
        section2To3Timer === null
      ) {
        section2RevealTimer = window.setTimeout(() => {
          section2RevealTimer = null;
          const stillInSection2NarrativeRange = window.scrollY >= section2.offsetTop - 8 && window.scrollY < section3.offsetTop - 8;
          if (
            liveSwapProgress >= section2NarrativeRevealSwapThreshold &&
            lastCardOpacity > section2NarrativeRevealOpacityThreshold &&
            stillInSection2NarrativeRange &&
            !section2NarrativeExiting &&
            section2To3Timer === null
          ) {
            const fromSection1To2 = liveSection3EntryProgress < 0.02;
            showSection2Narrative(fromSection1To2, fromSection1To2 ? section2NarrativeHeroHoldDelay : 0);
          }
        }, 140);
      }
    };
    shimmerLayer.addEventListener('animationend', onShimmerAnimationEnd);
    this.landingCleanup.push(() => shimmerLayer.removeEventListener('animationend', onShimmerAnimationEnd));

    const onMotionPrefChange = () => setDoodleMotion();
    prefersReducedMotion.addEventListener('change', onMotionPrefChange);
    this.landingCleanup.push(() => prefersReducedMotion.removeEventListener('change', onMotionPrefChange));

    resizeDoodleCanvas();
    setDoodleMotion();
    runIntroSequence();

    update();
    scrollSection4ToIndex(2, 'auto');
    updateSection4ActiveSlide();
    scheduleSection4Autoplay();
  }
